<script>
  import {
    router,
    Link,
    page,
    useForm,
  } from '@inertiajs/svelte'

  import "../../assets/stylesheets/fonts.css"

  import Main from "../layouts/_main.svelte"
  import PageTitle from "../layouts/_page_title.svelte"
  import ProgressBar from "./show/_progress_bar.svelte"
  import LoadingButton from "../components/_loading_button.svelte"

  import {
    configure,
    config,
    root_path,
    translation_path,
    download_translation_path,
    pay_translation_path
  } from '../../javascript/routes'

  import { subscribe, getStore } from 'livestores'
  import { onDestroy } from 'svelte'

  import { I18n } from "i18n-js"
  import i18n_translations from "../../javascript/locales.json"

  let { RailsApp, translation_id } = $props()

  const i18n = new I18n(i18n_translations)
  i18n.locale = RailsApp.I18n.locale

  configure({
    default_url_options: {locale: i18n.locale},
  })

  // Set up a subscription to the UserChannel
  const subscription = subscribe('TranslationChannel', {id: translation_id, locale: i18n.locale})

  const translation = getStore('translation')

  // Don't forget to unsubscribe when the component is destroyed
  //onDestroy(() => subscription.unsubscribe())
  onDestroy(() => {
    if (subscription && typeof subscription.unsubscribe === 'function') {
      subscription.unsubscribe()
    } else {
      //console.warn('Subscription does not have an unsubscribe method:', subscription)
    }
  })

  const form = useForm({
    translation: translation
  })

  let isDeletingInProgress = $state(false)

  function submit(event) {
    event.preventDefault()
    $form.patch(pay_translation_path(translation_id, {locale: RailsApp.I18n.locale}))
  }

  // toast
  import { Icon, Toast, ToastBody, ToastHeader } from '@sveltestrap/sveltestrap'
</script>

<PageTitle
  {...$page.props}
  page_title={
    $translation ? i18n.t(`Translations.Show.${$translation.status}.page_title`, {progress: $translation.progress, source_file_name: $translation.source_file_name}) : i18n.t('form_actions.in_progress')} />

<Main {...$page.props}>

  <section id="translate" class="container">
    {#if $translation}
      {#if $translation.paid_at && ['queued', 'working'].includes($translation.status) && $page && $page.url && (new URLSearchParams($page.url.split('?')[1] || '')).has('stripe_checkout_session_id')}
        <div class="toast-container position-fixed top-0 end-0 p-3">
        <Toast autohide delay={10000}>
          <ToastHeader>
            <span class="text-success">{i18n.t(`Translations.Show.${$translation.status}.toast.header`)}</span>
          </ToastHeader>
          <ToastBody>
            <span class="text-success">{i18n.t(`Translations.Show.${$translation.status}.toast.body`)}</span>
          </ToastBody>
        </Toast>
      </div>
      {/if}

     {@const description = i18n.t(`Translations.Show.${$translation.status}.description`, {progress: $translation.progress, source_file_name: $translation.source_file_name})}

      <h2 class="title mb-3">{i18n.t(`Translations.Show.${$translation.status}.title`, {source_file_name: $translation.source_file_name})}</h2>
      <div class="description mb-4">{description}</div>

      {#if $translation.status == 'created'}
        <div class="card text-bg-light shadow-sm mb-4" style="max-width: 540px;">
          <div class="card-body">
            <p class="card-title">{$translation.source_file_name}</p>
            <div class="card-text small">
              {$translation.source_language.name} <i class="bi bi-arrow-right"></i> {$translation.target_language.name}
              <br />
              {i18n.t(`Translations.Show.${$translation.status}.characters_count`, {count: i18n.numberToDelimited($translation.source_text_characters_count)})}
            </div>
          </div>
          <div class="card-footer">
            <div class="value"></div>
            <span class="fw-bold">{i18n.t(`Translations.Show.${$translation.status}.price`)} {i18n.numberToCurrency($translation.amount / 100, {format: '%u%n', unit: '$', precision: 2})}</span>
            <span class="text-muted small d-block">({i18n.numberToCurrency($translation.plan.price / 100, {format: '%u%n', unit: '$', precision: 2})} / {i18n.t(`Translations.Show.${$translation.status}.characters_count`, {count: i18n.numberToDelimited($translation.plan.max_characters_per_document)})})</span>
          </div>
        </div>
        <form novalidate onsubmit={submit}>
            <LoadingButton
              RailsApp={RailsApp}
              processing={$form.processing}
              type="submit"
              class="elementary-btn elementary-btn-deep-blue"
            >
              {#snippet content()}
                {i18n.t(`Translations.Show.${$translation.status}.cta`)}
              {/snippet}
            </LoadingButton>
        </form>
        <div class="text-muted mt-4 mb-3">{i18n.t('Translations.Show.removal.description_for_unpaid')}</div>
        <LoadingButton
          RailsApp={RailsApp}
          processing={isDeletingInProgress}
          type="button"
          class="elementary-btn elementary-btn-red"
          onclick={async () => {
            if (window.confirm(i18n.t('Translations.Show.removal.confirm'))) {
              isDeletingInProgress = true
              await router.delete(translation_path($translation.id))
            }
          }}
        >
          {#snippet content()}
            <i class="bi bi-trash"></i> {i18n.t('Translations.Show.removal.cta')}
          {/snippet}
        </LoadingButton>
        <div class="text-muted mt-4 border-top pt-4 mb-3">{i18n.t('Translations.Show.back.description')}</div>
        <Link aria-label="{i18n.t('Translations.Show.back.cta')}" href="{root_path()}">{i18n.t('Translations.Show.back.cta')}</Link>
      {:else if ($translation.status == 'queued')}
        <ProgressBar description={description} status={$translation.status} progress={100} />
      {:else if $translation.status == 'working'}
        <ProgressBar description={description} status={$translation.status} progress={$translation.progress} />
      {:else if ($translation.status == 'completed' || $translation.status == 'failed')}
        <div class="col-md-7 col-12">

          {#if $translation.status == 'completed'}
            <a class="d-inline-block elementary-btn elementary-btn-deep-blue" href="{download_translation_path($translation.id)}"><i class="bi bi-cloud-arrow-down"></i> {i18n.t('Translations.Show.completed.download')}</a>
          {/if}

          <div class="text-muted mt-4 mb-3">{i18n.t('Translations.Show.removal.description', {removal_scheduled_at: i18n.l("date.formats.message", $translation.removal_scheduled_at)})}</div>
          <LoadingButton
            RailsApp={RailsApp}
            processing={isDeletingInProgress}
            type="button"
            class="elementary-btn elementary-btn-red"
            onclick={async () => {
              if (window.confirm(i18n.t('Translations.Show.removal.confirm'))) {
                isDeletingInProgress = true
                await router.delete(translation_path($translation.id))
              }
            }}
          >
            {#snippet content()}
              <i class="bi bi-trash"></i> {i18n.t('Translations.Show.removal.cta')}
            {/snippet}
          </LoadingButton>

          <div class="text-muted mt-4 border-top pt-4 mb-3">{i18n.t('Translations.Show.back.description')}</div>
          <Link aria-label="{i18n.t('Translations.Show.back.cta')}" href="{root_path()}">{i18n.t('Translations.Show.back.cta')}</Link>
        </div>
      {:else if $translation.status == 'deleted'}
        <div class="text-muted mt-5 border-top pt-4 mb-3">{i18n.t('Translations.Show.back.description')}</div>
        <Link aria-label="{i18n.t('Translations.Show.back.cta')}" href="{root_path()}">{i18n.t('Translations.Show.back.cta')}</Link>
      {/if}
    {:else}
      <div class="description d-flex align-items-center">
        <img src="{RailsApp.Assets.spinner}" class="spinner me-1" alt="loading" />
        {i18n.t('form_actions.in_progress')}
      </div>
    {/if}
  </section>

</Main>
